import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {
    productHelper,
    Document,
    MIGRATION_RENEWAL_INVITE_EMAIL,
    MIGRATION_UPDATED_RENEWAL_INVITE_EMAIL,
} from "@ferry/journey-services";
import {FalconNavigatorHeader, Footer} from "@ferry/portal-core";
import {lazyInject} from "@athena-portal/core";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {FalconAccordion} from "../../../../../components/FalconAccordion";
import {DocumentListModal} from "@ferry/plugin-ferry-journey";
import {downloadDocument, trimFileExtension} from "../../../../../CommonFunctions";
import { mapCommunicationTitles } from "src/utils/DataMappers";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";
import {Types} from "../../../../../Types";

interface IState {
    interaction?: any;
    showLoading: boolean;
    documents: Document[];
    interactionFound?: boolean;
    isDocumentDownloaded: boolean;
}

@authorize
export class MigratedRenewalDocumentsPage extends React.Component<{}, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private readonly FILE_PATH: string = "path";
    private readonly FILE_NAME: string = "fileName";
    private readonly FILE_ID: string = "id";
    private readonly FILE_LABEL: string = "label";

    constructor(props: Readonly<{}>) {
        super(props);
        this.state = {
            showLoading: true,
            documents: [],
            isDocumentDownloaded: true
        };
        this.createPDFMap = this.createPDFMap.bind(this);

    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Renewal documents");

        this.customerPortalService.getInteractionByStatus(['MIGRATED_RENEWAL', 'MIGRATED_RENEWAL_ACCEPTED'])
            .subscribe((interaction: any) => {
                if (interaction) {
                    this.customerPortalService.getCommunicationsByQuoteId(interaction._id).subscribe((documents: Document[]) => {

                        const hasOldDoc = documents.some(doc => doc.name.includes(MIGRATION_RENEWAL_INVITE_EMAIL));
                        const hasUpdatedDoc = documents.some(doc => doc.name.includes(MIGRATION_UPDATED_RENEWAL_INVITE_EMAIL));

                        const filteredDocuments: Document[] = documents.filter(doc => {
                            if (hasOldDoc && hasUpdatedDoc) {
                                return doc.name.includes(MIGRATION_UPDATED_RENEWAL_INVITE_EMAIL);
                            }
                            return doc.name.includes(MIGRATION_RENEWAL_INVITE_EMAIL) || doc.name.includes(MIGRATION_UPDATED_RENEWAL_INVITE_EMAIL);
                        });

                        const mappedDocuments: Document[] = [];
                        mappedDocuments.push(...mapCommunicationTitles(filteredDocuments));

                        this.setState({
                            documents: mappedDocuments
                        });
                    })

                    this.setState({interaction, showLoading: false, interactionFound: true});
                } else {
                    this.setState({showLoading: false, interactionFound: false});
                }
            })
    }

    public render() {

        return (
            <React.Fragment>
                <FalconHeader/>

                <div className="plugin">
                    <FalconNavigatorHeader
                        back={`/dashboard`}
                        title="Your car insurance documents"
                    />
                    {!this.state.showLoading && <div className="documents-page">
                        {this.renderDocumentsPanel()}
                    </div>}
                    {this.state.interaction && this.pluginTemplates.renderBackLink("Back", false, `/dashboard`)}
                </div>
                <Overlay show={this.state.showLoading}/>
                <Footer/>
            </React.Fragment>
        );
    }

    private renderDocumentsPanel() {
        if (this.state.interaction){
            return (
                <div className="widget documents-widget active-policy-documents-widget panel">
                    <p className="active-policy-documents-title font-weight-bold text-center fs-18">Renewal documents</p>
                    <ul className="list-unstyled">
                        {this.state.documents.length > 0 && this.renderDocuments(this.state.documents)}
                        <FalconAccordion toggleHidden={<div className="toggle">+ Show more</div>}
                                         toggleShown={<div className="toggle">- Show less</div>}>
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.interaction?.product)}
                                policyDate={new Date(this.state.interaction.factors.originalQuoteDate)}
                                isMLEVisible={this.state.interaction.optionalExtras.motorLegalExpenses}
                                isAccordion={false}
                                coverTier={this.state.interaction.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
        } else {
            return <></>;
        }
    }

    private renderDocuments(documents: Document[]): React.ReactNode {
        return documents.map((document: Document, index: number) => (
            <li className="media" key={index}>
                <div className="media-body">
                    <div className="row height-0">
                        <div className="col-1"/>
                        <div className="col-11">
                            <div className="dashed-10 ml-3 mr-3"/>
                        </div>
                    </div>
                    <a className="document-download-link"
                        onClick={this.downloadDocument(document)}
                       id={trimFileExtension(document.name)}
                    >
                        <img className="pdf-icon active-icon" alt="Document download icon"
                             src="/img/pdf-icon.png"/>
                        <h5 className="pt-2 document-name">{document.title}</h5>
                    </a>
                </div>
            </li>
        ));
    }

    private createPDFMap(path: string, fileName: string, id: string, label: string): Map<string, string> {
        return new Map()
            .set(this.FILE_PATH, path)
            .set(this.FILE_NAME, fileName)
            .set(this.FILE_ID, id)
            .set(this.FILE_LABEL, label);
    }

    private downloadDocument = (doc: Document) => (event: any): void => {
        this.setState({isDocumentDownloaded: false});
        this.customerPortalService.downloadDocument(doc.id).subscribe(
            (documentContent: ArrayBuffer) => {
                if (documentContent) {
                    downloadDocument(documentContent, doc.downloadName);
                }
                this.setState({isDocumentDownloaded: true});
            }
        );
    };
}
